// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Skipper tracker, checks, downloads (Phase 3), § API contract (skipper), § Portal routes
// ==============================================
// /dashboard/papers/requests/:rid[?vessel=<vid>] — the skipper's tracker
// (Skipper-Tracker artboard, in the dashboard's slate). The withdrawal and
// status emails link here without a vessel, so the page finds the vessel
// among the ones I own or captain.
//
//   header      status, passage, dates and time zone; Edit request, the crew
//               list for Customs (PDF / CSV), "Remind the N now"
//   tiles       in · needs your look · waiting · due in N days
//   grid        crew × papers, a cell per paper (tracker.ts); per person:
//               remind, resend link, ask to extend
//   panels      "Needs your look" (ask them about it, or accept it as is) and
//               "Coming up" (from the API: the scheduler's own rules)
//
// Opening a shared paper is a view in the crew member's activity; Download
// gets a copy stamped with the line the preview shows over it, and is a
// download in their activity. Nothing under 13 px (readability.test.ts).
// ==============================================

import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../services/api';
import { apiError, waitLabel } from '../apiError';
import { accessEndDate, firstName, fmtDay, fmtDayYear, passageLabel, today as todayIn } from '../dates';
import type { GrantedPaper, RequestDetail, SkipperRecipientView } from '../types';
import { FormError, S, SkipperConfirm } from './skipperUi';
import { STATUS_WORD, cellFor, comingUpText, crewName, dueTile, hasActiveGrant, lookLines, paperColumns, remindable, rowSummary } from './tracker';
import type { CellTone, LookLine } from './tracker';

/** The vessel a request of mine is on: the ?vessel hint, else a look through my skipper vessels. */
async function findVessel(rid: string, hint: string | null): Promise<string> {
  if (hint) return hint;
  const access = await api.papersAccess();
  for (const v of access.vessels.filter((x) => x.role === 'owner' || x.role === 'captain')) {
    const list = await api.skipperRequests(v.id).catch(() => []);
    if (list.some((r) => r.id === rid)) return v.id;
  }
  throw Object.assign(new Error('This request isn’t one of yours, or it no longer exists.'), { notFound: true });
}

/** Hand a blob to the browser as a file. */
function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

const TONE: Record<CellTone, string> = {
  in: 'bg-emerald-500/10 text-emerald-300',
  wait: 'bg-amber-400/10 text-amber-200',
  look: 'bg-red-500/10 text-red-300 border border-red-400/50',
  na: 'bg-slate-700/40 text-slate-300',
  off: 'border border-dashed border-slate-700 text-slate-400',
  ended: 'bg-slate-700/40 text-slate-400',
};

// ---------------- a shared paper: view, download (stamped), with the stamp shown over it ----------------

function Watermark({ text }: { text: string }) {
  if (!text) return null;
  return (
    <div aria-hidden="true" data-testid="watermark" className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg">
      <div className="absolute left-1/2 top-1/2 w-[140%] -translate-x-1/2 -translate-y-1/2 -rotate-[30deg] text-center text-[22px] font-semibold text-slate-500/25">{text}</div>
      <div className="absolute inset-x-0 bottom-0 bg-white/85 px-3 py-1 text-[13px] text-slate-700">{text}</div>
    </div>
  );
}

function PaperModal({ vid, rid, itemId, crew, tz, onClose }: { vid: string; rid: string; itemId: string; crew: string; tz: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ['skipper-paper', rid, itemId], queryFn: () => api.skipperGrantedPaper(vid, rid, itemId), retry: false, staleTime: 0, gcTime: 0 });
  const [file, setFile] = useState<{ url: string; mime: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const p: GrantedPaper | undefined = q.data;
  const owner = firstName(p?.paper.ownerName, crew);

  const view = async (fid: string, mime: string, name: string) => {
    setBusy(`view:${fid}`); setError(null);
    try { const b = await api.skipperGrantedFileBlob(vid, rid, itemId, fid); if (file) URL.revokeObjectURL(file.url); setFile({ url: URL.createObjectURL(b), mime, name }); } catch (err) { setError(apiError(err, 'The file could not be opened.').message); } finally { setBusy(null); }
  };
  const download = async (fid: string, name: string) => {
    setBusy(`dl:${fid}`); setError(null); setNotice(null);
    try {
      const b = await api.skipperGrantedFileBlob(vid, rid, itemId, fid, true);
      saveBlob(b, `${name.replace(/\.[A-Za-z0-9]{1,5}$/, '')}.pdf`);
      setNotice(`Downloaded, marked with your name. ${owner} can see that you did.`);
    } catch (err) { setError(apiError(err, 'The download didn’t work. Try again.').message); } finally { setBusy(null); }
  };
  const close = () => { if (file) URL.revokeObjectURL(file.url); onClose(); };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => () => { if (file) URL.revokeObjectURL(file.url); }, [file]);

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 p-3" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="paper-title" className="flex max-h-[92vh] w-full max-w-3xl flex-col gap-4 overflow-y-auto rounded-2xl border border-slate-700 bg-slate-800 p-5">
        <div className="flex items-start justify-between gap-3">
          <h2 id="paper-title" className="m-0 text-lg font-bold text-white">{p ? `${p.item.label} — ${p.paper.ownerName ?? crew}` : 'Opening…'}</h2>
          <button type="button" onClick={close} className={S.ghost} autoFocus>Close</button>
        </div>
        {q.error && <FormError>{apiError(q.error, 'This paper isn’t shared with you right now.').message}</FormError>}
        <FormError>{error}</FormError>
        {notice && <p role="status" className="m-0 text-[15px] text-emerald-300">{notice}</p>}
        {p && (
          <>
            <p className="m-0 text-sm text-slate-400">Shared with you until {fmtDayYear(accessEndDate(p.grant.expiresAt, tz))}. {owner} sees each time you open or download it. Downloads are marked “{p.watermark}”.</p>
            <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-2 text-base sm:grid-cols-2">
              {p.paper.expiresOn && <div><dt className="text-sm text-slate-400">Expires</dt><dd className="m-0 text-white">{fmtDayYear(p.paper.expiresOn)}</dd></div>}
              {Object.entries(p.details ?? {}).filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><dt className="text-sm capitalize text-slate-400">{k.replace(/_/g, ' ')}</dt><dd className="m-0 break-words text-white">{v}</dd></div>
              ))}
            </dl>
            {p.files.map((f) => (
              <div key={f.id} className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-base text-slate-200">{f.fileName}</span>
                <button type="button" className={S.ghost} disabled={!!busy} onClick={() => void view(f.id, f.mime, f.fileName)}>View</button>
                <button type="button" className={S.ghost} disabled={!!busy} onClick={() => void download(f.id, f.fileName)}>{busy === `dl:${f.id}` ? 'Marking…' : 'Download'}</button>
              </div>
            ))}
            {file && (
              <div className="relative">
                {file.mime.startsWith('image/')
                  ? <img src={file.url} alt={file.name} className="max-h-[60vh] w-full rounded-lg bg-black/40 object-contain" />
                  : <iframe title={file.name} src={file.url} className="h-[60vh] w-full rounded-lg bg-white" />}
                <Watermark text={p.watermark} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------- "Needs your look": ask or accept ----------------

function LookItem({ line, busy, onAsk, onAccept }: { line: LookLine; busy: boolean; onAsk: (note: string) => Promise<boolean>; onAccept: () => void }) {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState(line.question ?? line.draft);
  return (
    <li className="flex flex-col gap-3 border-t border-slate-700/60 pt-4 first:border-0 first:pt-0">
      <span className="text-[17px] font-bold text-white">{line.title}</span>
      <span className="text-[15px] leading-relaxed text-slate-300">{line.body}</span>
      {line.question && !asking && <span className="text-[15px] leading-relaxed text-slate-300">You asked: “{line.question}” It goes in {line.crew}’s next reminder.</span>}
      {asking ? (
        <form className="flex flex-col gap-2" onSubmit={async (e) => { e.preventDefault(); if (await onAsk(note)) setAsking(false); }}>
          <label htmlFor={`ask-${line.itemId}`} className="text-sm font-semibold text-slate-200">Your question to {line.crew}</label>
          <textarea id={`ask-${line.itemId}`} rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} className={`${S.input} py-2 leading-snug`} />
          <span className="text-sm text-slate-400">It shows on {line.crew}’s paper and goes in their next reminder.</span>
          <span className="flex flex-wrap gap-2">
            <button type="submit" className={S.primary} disabled={busy || !note.trim()}>{busy ? 'Saving…' : `Ask ${line.crew}`}</button>
            <button type="button" className={S.ghost} onClick={() => setAsking(false)}>Cancel</button>
          </span>
        </form>
      ) : (
        <span className="flex flex-wrap gap-2">
          <button type="button" className={S.primary} onClick={() => setAsking(true)} disabled={busy}>{line.question ? 'Change the question' : `Ask ${line.crew} about it`}</button>
          <button type="button" className={S.ghost} onClick={onAccept} disabled={busy}>Accept as is</button>
        </span>
      )}
    </li>
  );
}

// ---------------- the page ----------------

export function RequestDetailPage() {
  const { rid = '' } = useParams();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const vesselQ = useQuery({ queryKey: ['skipper-request-vessel', rid, params.get('vessel')], queryFn: () => findVessel(rid, params.get('vessel')), retry: false });
  const vid = vesselQ.data ?? null;
  const q = useQuery({ queryKey: ['skipper-request', vid, rid], queryFn: () => api.skipperRequest(vid as string, rid), enabled: !!vid, retry: false });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | 'cancel' | 'send' | 'remind'>(null);
  const [opening, setOpening] = useState<{ itemId: string; crew: string } | null>(null);

  const put = (d: RequestDetail) => { qc.setQueryData(['skipper-request', vid, rid], d); qc.invalidateQueries({ queryKey: ['skipper-requests', vid] }); };
  const act = async (key: string, fn: () => Promise<RequestDetail>, done: (d: RequestDetail) => string): Promise<boolean> => {
    setBusy(key); setError(null); setNotice(null);
    try { const d = await fn(); put(d); setNotice(done(d)); return true; } catch (err) {
      const e = apiError(err, 'That didn’t go through. Try again.');
      setError(e.status === 429 && e.retryAfter ? `${e.message}. Try again in ${waitLabel(e.retryAfter)}.` : e.message);
      return false;
    } finally { setBusy(null); setConfirm(null); }
  };
  const crewList = async (format: 'pdf' | 'csv') => {
    setBusy(`list:${format}`); setError(null); setNotice(null);
    try {
      const { blob, fileName } = await api.skipperCrewList(vid as string, rid, format);
      saveBlob(blob, fileName);
      setNotice('The crew list is downloaded. Each crew member can see that their passport was used for it.');
    } catch (err) { setError(apiError(err, 'The crew list couldn’t be made. Try again.').message); } finally { setBusy(null); }
  };

  if (vesselQ.isLoading || q.isLoading) return <div className="p-10 text-center text-base text-slate-400" role="status">Loading…</div>;
  if (vesselQ.error || q.error || !q.data || !vid) {
    return (
      <div className="mx-auto max-w-xl space-y-3 p-6">
        <FormError>{vesselQ.error instanceof Error && !('isAxiosError' in vesselQ.error) ? vesselQ.error.message : apiError(vesselQ.error ?? q.error, 'This request could not be loaded.').message}</FormError>
        <Link to="/dashboard/papers/requests" className={S.link}>All my requests</Link>
      </div>
    );
  }

  const d = q.data;
  const tz = d.timezone;
  const today = todayIn(tz);
  const passage = passageLabel(d.fromPlace, d.toPlace);
  const live = d.status === 'open' || d.status === 'complete';
  const failures = d.emailFailures ?? [];
  const columns = paperColumns(d);
  const due = dueTile(d);
  const looks = lookLines(d);
  const toRemind = remindable(d);
  const word = STATUS_WORD[d.status];
  const names = (rs: SkipperRecipientView[]) => rs.map((r) => firstName(crewName(r), crewName(r)));
  const remindOne = (r: SkipperRecipientView) => act(`remind:${r.id}`, () => api.skipperRemindNow(vid, d.id, r.id), (x) => (x.emailFailures?.includes(r.id) ? `The reminder to ${crewName(r)} didn’t go. Try again in a moment.` : `${crewName(r)} has a reminder of what they still owe.`));
  const remindedMsg = (x: RequestDetail) => {
    const sent = x.reminded?.length ?? 0;
    const skipped = x.skipped?.filter((s) => s.reason !== 'done').length ?? 0;
    const failed = x.emailFailures?.length ?? 0;
    return [sent ? `${sent} reminder${sent === 1 ? '' : 's'} sent.` : 'No reminders sent.', skipped ? `${skipped} ${skipped === 1 ? 'person was' : 'people were'} reminded recently, so not again yet.` : '', failed ? `${failed} didn’t go — try again in a moment.` : ''].filter(Boolean).join(' ');
  };

  return (
    <div className="mx-auto max-w-[1280px] space-y-6 p-3 sm:p-6">
      {/* ---- header ---- */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex min-w-0 flex-col gap-2">
          <nav aria-label="Breadcrumb" className={S.label}>
            <Link to={`/dashboard/papers?vessel=${vid}`} className="hover:text-white">Ship’s Papers</Link> › <Link to={`/dashboard/papers/requests?vessel=${vid}`} className="hover:text-white">Crew papers requests</Link>
          </nav>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className={`h-3 w-3 rounded-full ${word.dot}`} aria-hidden="true" />
            <span className={`text-[17px] font-bold tracking-[0.08em] ${word.text}`}>{word.word}</span>
            <span className={S.note}>· {fmtDay(today)}</span>
          </div>
          <h1 className="m-0 text-3xl font-bold text-white">{passage ? `${passage} · crew papers` : 'Crew papers request'}</h1>
          <p className="m-0 text-base text-slate-300">
            {d.departOn ? `Sails ${fmtDayYear(d.departOn)}` : 'No departure date'}{d.arriveOn ? ` · arrives ${fmtDay(d.arriveOn)}` : ''}
            {d.suggestedAccessEnd ? ` · you suggested access until ${fmtDay(d.suggestedAccessEnd)}` : ''}
          </p>
          <p className="m-0 text-sm text-slate-400">Emails go at 08:00 {tz} time{d.closesOn && live ? ` · links work until ${fmtDay(d.closesOn)}` : ''}</p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {(d.status === 'draft' || live) && <Link to={`/dashboard/papers/requests/${d.id}/edit?vessel=${vid}`} className={S.ghost}>{d.status === 'draft' ? 'Edit draft' : 'Edit request'}</Link>}
          {d.status !== 'draft' && (
            <span className="inline-flex items-center gap-1 rounded-xl border border-slate-600 pl-3" role="group" aria-label="Crew list for Customs">
              <span className="text-[15px] font-semibold text-white">Crew list</span>
              <button type="button" aria-label="Crew list as PDF" className="min-h-[44px] rounded-lg px-3 text-[15px] font-semibold text-amber-300 hover:text-amber-200 disabled:opacity-50" disabled={!!busy} onClick={() => void crewList('pdf')}>{busy === 'list:pdf' ? '…' : 'PDF'}</button>
              <button type="button" aria-label="Crew list as CSV" className="min-h-[44px] rounded-lg px-3 text-[15px] font-semibold text-amber-300 hover:text-amber-200 disabled:opacity-50" disabled={!!busy} onClick={() => void crewList('csv')}>{busy === 'list:csv' ? '…' : 'CSV'}</button>
            </span>
          )}
          {d.status === 'open' && (
            <button type="button" className={S.primary} disabled={!toRemind.length || !!busy} onClick={() => setConfirm('remind')}>
              {toRemind.length ? `Remind the ${toRemind.length} now` : 'Everyone was reminded recently'}
            </button>
          )}
        </div>
      </div>

      {notice && <p role="status" className="m-0 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-base text-emerald-200">{notice}</p>}
      <FormError>{error}</FormError>
      {d.status === 'draft' && (
        <div className={`${S.card} flex flex-wrap items-center gap-3 p-4`}>
          <span className="flex-1 text-base text-slate-200">This is a draft — nobody has been emailed yet.</span>
          <button type="button" className={S.primary} onClick={() => setConfirm('send')}>Send to {d.recipients.length} crew</button>
        </div>
      )}

      {/* ---- tiles ---- */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className={`${S.card} flex flex-col gap-1 px-5 py-4`}><span className={S.label}>In</span><span className="font-mono text-[40px] leading-none text-white">{d.stats.in}<span className="ml-1.5 text-[19px] text-slate-400">of {d.stats.total}</span></span></div>
        <div className={`${S.card} flex flex-col gap-1 px-5 py-4`}><span className={S.label}>Needs your look</span><span className={`font-mono text-[40px] leading-none ${d.stats.attention ? 'text-red-300' : 'text-white'}`}>{d.stats.attention}</span></div>
        <div className={`${S.card} flex flex-col gap-1 px-5 py-4`}><span className={S.label}>Waiting</span><span className={`font-mono text-[40px] leading-none ${d.stats.waiting ? 'text-amber-300' : 'text-white'}`}>{d.stats.waiting}</span></div>
        {due ? <div className={`${S.card} flex flex-col gap-1 px-5 py-4`}><span className={S.label}>{due.label}</span><span className="font-mono text-[40px] leading-none text-white">{due.value}<span className="ml-1.5 text-[19px] text-slate-400">{due.unit}</span></span></div>
          : <div className={`${S.card} flex flex-col gap-1 px-5 py-4`}><span className={S.label}>Due</span><span className="text-base text-slate-400">No due date yet</span></div>}
      </div>

      {/* ---- the grid ---- */}
      <section className={`${S.card} p-4 sm:p-5`} aria-label="Crew and papers">
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full min-w-[760px] border-separate border-spacing-2 text-left">
            <caption className="sr-only">Each crew member’s papers</caption>
            <thead>
              <tr>
                <th scope="col" className={`${S.label} w-[230px] align-bottom`}>Crew</th>
                {columns.map((c) => <th key={c.type} scope="col" className={`${S.label} align-bottom`}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {d.recipients.map((r) => {
                const crew = crewName(r);
                const first = firstName(crew, crew);
                return (
                  <tr key={r.id}>
                    <th scope="row" className="align-top font-normal">
                      <div className="flex flex-col gap-0.5 py-1">
                        <span className="text-base font-bold text-white">{crew}</span>
                        <span className="text-sm text-slate-400">{rowSummary(r)}{!r.linkSent ? ' · not sent yet' : r.claimed ? '' : ' · hasn’t opened the link'}</span>
                        {failures.includes(r.id) && <span className="text-sm text-red-300">The email to {first} didn’t go — try again.</span>}
                        {live && (
                          <span className="mt-1 flex flex-wrap gap-x-3">
                            {d.status === 'open' && !r.done && r.outstanding > 0 && (
                              <button type="button" className={`min-h-[44px] text-[15px] ${S.link} disabled:opacity-50`} disabled={!!busy || !!r.remindAfter}
                                title={r.remindAfter ? 'Reminded recently — nobody is reminded twice within 12 hours' : undefined}
                                onClick={() => void remindOne(r)}>{busy === `remind:${r.id}` ? 'Sending…' : r.remindAfter ? 'Reminded recently' : `Remind ${first}`}</button>
                            )}
                            <button type="button" className={`min-h-[44px] text-[15px] ${S.link} disabled:opacity-50`} disabled={!!busy}
                              onClick={() => void act(`resend:${r.id}`, () => api.skipperResendLink(vid, d.id, r.id), (x) => (x.emailFailures?.includes(r.id) ? `We couldn’t email ${crew}. Check the address and try again.` : `A new link is on its way to ${crew}. The old one no longer works.`))}>
                              {busy === `resend:${r.id}` ? 'Sending…' : 'Resend link'}
                            </button>
                            {hasActiveGrant(r) && <Link to={`/dashboard/papers/requests/${d.id}/extend?vessel=${vid}&recipient=${r.id}`} className={`inline-flex min-h-[44px] items-center text-[15px] ${S.link}`}>Ask {first} to extend</Link>}
                          </span>
                        )}
                      </div>
                    </th>
                    {columns.map((c) => {
                      const i = r.items.find((x) => x.type === c.type);
                      const cell = cellFor(i, r, tz, today);
                      const canOpen = !!(i?.grant?.active && i.paper);
                      const inner = (
                        <>
                          <span className="text-[15px] font-semibold">{cell.title}</span>
                          {cell.sub && <span className="text-[13px] font-medium text-slate-300">{cell.sub}</span>}
                        </>
                      );
                      return (
                        <td key={c.type} className="align-top">
                          {canOpen && i
                            ? <button type="button" onClick={() => setOpening({ itemId: i.id, crew })} aria-label={`Open ${first}’s ${c.label.toLowerCase()}: ${cell.title}`}
                                className={`flex min-h-[66px] w-full flex-col justify-center gap-1 rounded-xl p-3 text-left hover:brightness-125 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70 ${TONE[cell.tone]}`}>{inner}</button>
                            : <div className={`flex min-h-[66px] flex-col justify-center gap-1 rounded-xl p-3 ${TONE[cell.tone]}`}>{inner}</div>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---- panels ---- */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section className={`${S.card} flex flex-col gap-4 p-5 ${looks.length ? '!border-red-400/50' : ''}`} aria-labelledby="look-title">
          <h2 id="look-title" className={`${S.label} m-0 ${looks.length ? '!text-red-300' : ''}`}>Needs your look</h2>
          {looks.length ? (
            <ul className="m-0 flex list-none flex-col gap-4 p-0">
              {looks.map((l) => (
                <LookItem key={l.itemId} line={l} busy={busy === `look:${l.itemId}`}
                  onAsk={(note) => act(`look:${l.itemId}`, () => api.skipperAskAbout(vid, d.id, l.itemId, note), () => `Your question goes in ${l.crew}’s next reminder, and shows on the paper.`)}
                  onAccept={() => void act(`look:${l.itemId}`, () => api.skipperAcceptAsIs(vid, d.id, l.itemId), () => `Accepted. ${l.crew}’s paper counts as in.`)} />
              ))}
            </ul>
          ) : <p className="m-0 text-base text-slate-400">Nothing needs your look. A paper that expires before you need it would show here.</p>}
        </section>
        <section className={`${S.card} flex flex-col gap-3 p-5`} aria-labelledby="coming-title">
          <h2 id="coming-title" className={`${S.label} m-0`}>Coming up</h2>
          {d.comingUp.length ? (
            <ul className="m-0 flex list-none flex-col gap-2.5 p-0 text-[15px]">
              {d.comingUp.map((c, n) => {
                const t = comingUpText(c);
                return (
                  <li key={`${c.kind}-${c.on}-${n}`} className="flex gap-4">
                    <span className={`w-[110px] shrink-0 font-mono ${t.due ? 'text-amber-300' : 'text-slate-300'}`}>{fmtDay(t.on)}</span>
                    <span className={t.due ? 'text-amber-300' : 'text-white'}>{t.text}</span>
                  </li>
                );
              })}
            </ul>
          ) : <p className="m-0 text-base text-slate-400">Nothing planned.</p>}
        </section>
      </div>

      {(d.status === 'draft' || live) && (
        <div className="flex justify-end">
          <button type="button" className={S.danger} onClick={() => setConfirm('cancel')}>Cancel this request</button>
        </div>
      )}

      {confirm === 'remind' && (
        <SkipperConfirm title={`Remind the ${toRemind.length} now?`} confirmLabel="Send reminders" busy={busy === 'remind'}
          onCancel={() => setConfirm(null)} onConfirm={() => void act('remind', () => api.skipperRemindNow(vid, d.id), remindedMsg)}>
          {names(toRemind).join(', ')} each get a reminder now, listing only what they still owe. It counts as today’s reminder, and nobody is reminded twice within 12 hours.
        </SkipperConfirm>
      )}
      {confirm === 'cancel' && (
        <SkipperConfirm title="Cancel this request?" confirmLabel="Cancel request" cancelLabel="Keep it" danger busy={busy === 'cancel'}
          onCancel={() => setConfirm(null)} onConfirm={() => void act('cancel', () => api.skipperCancelRequest(vid, d.id), () => 'The request is cancelled. The links no longer work, and every paper shared under it is withdrawn.')}>
          The crew’s links stop working, and every paper they shared with you under this request is withdrawn at once. Their papers stay in their own papers.
        </SkipperConfirm>
      )}
      {confirm === 'send' && (
        <SkipperConfirm title={`Send to ${d.recipients.length} crew?`} confirmLabel="Send now" busy={busy === 'send'}
          onCancel={() => setConfirm(null)} onConfirm={() => void act('send', () => api.skipperSendRequest(vid, d.id), (x) => (x.emailFailures?.length ? `Sent, but ${x.emailFailures.length} email${x.emailFailures.length === 1 ? '' : 's'} didn’t go — use Resend link.` : `Sent to ${x.recipients.length} crew.`))}>
          Each person gets an email now with their own link.
        </SkipperConfirm>
      )}
      {opening && <PaperModal vid={vid} rid={d.id} itemId={opening.itemId} crew={opening.crew} tz={tz} onClose={() => setOpening(null)} />}
    </div>
  );
}

export default RequestDetailPage;
